import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FiChevronLeft } from 'react-icons/fi';
import { FiChevronRight } from 'react-icons/fi';
import Text from '@/components/common/Text';

import usePagination, { UsePaginationProps } from '@/hooks/usePagination';

import { Theme } from '@/styles/Theme';

interface PaginationProps extends UsePaginationProps {
  showInfo?: boolean; // Optional: Show "Showing X-Y of Z" text
  itemName?: string; // Optional: Custom item name (default: "items")
}

const Pagination = ({ page, totalCount, size, showInfo = false, itemName = 'items' }: PaginationProps) => {
  const {
    pageNumberArray,
    handleNumberClick,
    handleRightArrowClick,
    handleLeftArrowClick,
    isFirstList,
    isLastList,
  } = usePagination({
    page,
    totalCount,
    size,
  });

  // Calculate showing range
  const start = totalCount === 0 ? 0 : (page - 1) * size + 1;
  const end = Math.min(page * size, totalCount);

  return (
    <Container>
      {totalCount > 0 && (
        <PaginationControls>
          <IconButton disabled={page === 1} onClick={handleLeftArrowClick}>
            <FiChevronLeft size='1.25rem' />
          </IconButton>
          <NumberContainer>
            {pageNumberArray.map((number) => (
              <NumberButton
                key={number}
                isSelected={page === number}
                onClick={handleNumberClick}
              >
                {number}
              </NumberButton>
            ))}
          </NumberContainer>
          <IconButton disabled={page === Math.ceil(totalCount / size)} onClick={handleRightArrowClick}>
            <FiChevronRight size='1.25rem' />
          </IconButton>
        </PaginationControls>
      )}
    </Container>
  );
};

export default Pagination;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1.5rem;
  padding: 1rem 0;
  gap: 0.75rem;
`;

const InfoText = styled(Text)`
  font-size: 0.875rem;
  color: ${Theme.colors.G_2};
  white-space: nowrap;
  margin-bottom: 0.25rem;
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.375rem;
`;

const IconButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 0.0625rem solid ${Theme.colors.G_4};
  border-radius: 0.375rem;
  background-color: ${Theme.colors.WHITE};
  color: ${Theme.colors.G_1};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background-color: ${Theme.colors.B_1};
    border-color: ${Theme.colors.B_1};
    color: ${Theme.colors.WHITE};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    background-color: ${Theme.colors.G_6};
    border-color: ${Theme.colors.G_4};
    color: ${Theme.colors.G_3};
  }
`;

const NumberContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const NumberButton = styled.button<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.25rem;
  height: 2.25rem;
  padding: 0 0.625rem;
  border: 0.0625rem solid ${({ isSelected }) => (isSelected ? Theme.colors.B_1 : Theme.colors.G_4)};
  border-radius: 0.375rem;
  background-color: ${({ isSelected }) => (isSelected ? Theme.colors.B_1 : Theme.colors.WHITE)};
  color: ${({ isSelected }) => (isSelected ? Theme.colors.WHITE : Theme.colors.G_1)};
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 500)};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background-color: ${({ isSelected }) => (isSelected ? Theme.colors.B_2 : Theme.colors.B_1)};
    border-color: ${Theme.colors.B_1};
    color: ${Theme.colors.WHITE};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
